import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAdminStatus = async () => {
    try {
      const response = await api.get('/admin/debug-auth');
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('access_token');

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          // Fetch admin status from backend
          await fetchAdminStatus();
        } catch (e) {
          // Corrupted user data - clear and force re-login
          localStorage.removeItem('user');
          localStorage.removeItem('access_token');
          setIsAuthenticated(false);
          setUser(null);
          delete api.defaults.headers.common['Authorization'];
        }
      }
    } else {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuthStatus();

    // Listen for storage changes (for when OAuth completes)
    const handleStorageChange = () => {
      checkAuthStatus();
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events
    window.addEventListener('authChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChanged', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (googleToken) => {
    try {
      const response = await api.post('/auth/google/login', {
        token: googleToken
      });

      const { access_token, user: userData } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(userData);
      setIsAuthenticated(true);

      // Fetch admin status after successful login
      await fetchAdminStatus();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
  };

  const value = {
    user,
    isAuthenticated,
    isAdmin,
    loading,
    login,
    logout,
    refreshAuth: checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};